import React from "react";
import qs from "query-string";
import { Box, LegacyStack, Text, IndexTable, Tooltip } from "@shopify/polaris";
import placeholderImg from "images/placeholder.png";
import { addShopParamToUrl, formatMoney, formattedVariantTitle } from "../../../helper_functions/utils";
import { QuantityChangeModal } from "../QuantityChangeModal";
import { TruncatedText } from "./TruncatedText";
import { LinkText } from "./LinkText";
import type { HorseVariantIndexRow } from "../../../api_utils/types";
import { Link } from "react-router-dom";
import type { VariantColumn } from "../../horse_variants/index/optionalColumns";

export function HorseVariantRow({
  horseVariant,
  userCurrency,
  setErrorMessage,
  position,
  visibleColumns,
}: {
  readonly horseVariant: HorseVariantIndexRow;
  readonly userCurrency: string;
  readonly setErrorMessage: (errorMessage: string) => void;
  readonly position: number;
  readonly visibleColumns: readonly VariantColumn[];
}): React.ReactElement {
  const isOnTimeMachineView = !!qs.parse(window.location.search).date;
  const showIncoming = visibleColumns.some((column) => column.id === "incoming");
  const showOnHand = visibleColumns.some((column) => column.id === "on_hand");
  const visibleLocationColumns = visibleColumns.filter((column) => "horseLocationId" in column);
  const visibleTrailingColumns = visibleColumns.filter(
    (column) => column.id !== "incoming" && column.id !== "on_hand" && !("horseLocationId" in column),
  );

  const {
    id,
    image_src,
    product_title,
    variant_title,
    sku,
    vendor,
    product_type,
    currently_available,
    committed,
    weight,
    cost,
    value,
    discarded,
    tracked,
  } = horseVariant;

  const column_color = discarded ? "rgba(142,147,152)" : "";

  let imgSrc = placeholderImg;
  if (image_src) {
    imgSrc = `${image_src}&height=58`;
  }

  const col0Markup = (
    <div className="horse-variant" style={{ overflow: "hidden" }}>
      <LegacyStack alignment="center" distribution="leading" key={id} spacing="tight" wrap={false}>
        <LegacyStack.Item>
          <span className="Polaris-Thumbnail">
            <img alt={`${product_title} variant`} className="product-photo" loading="lazy" src={imgSrc} />
          </span>
          <div id="PolarisPortalsContainer" />
        </LegacyStack.Item>

        <LegacyStack.Item>
          <TruncatedText>
            <LinkText href={`/horse_variants/${id}`}>
              <Text as="span" truncate>
                {product_title}
              </Text>
            </LinkText>
          </TruncatedText>
        </LegacyStack.Item>
      </LegacyStack>
    </div>
  );

  const variantTitle = formattedVariantTitle(variant_title);
  const col1Markup = variantTitle ? (
    <Link className="Polaris-Link Polaris-Link--removeUnderline" to={addShopParamToUrl(`/horse_variants/${id}`)}>
      {variantTitle}
    </Link>
  ) : null;

  const col2Markup = (
    <Link className="Polaris-Link Polaris-Link--removeUnderline" to={addShopParamToUrl(`/horse_variants/${id}`)}>
      {sku}
    </Link>
  );

  const col3Markup = (
    <span className="truncate" style={{ color: column_color }}>
      {vendor}
    </span>
  );

  const col4Markup = (
    <span className="truncate" style={{ color: column_color }}>
      {product_type}
    </span>
  );

  const available_display: string = tracked ? (currently_available || 0).toString() : "∞";

  const col5Markup = isOnTimeMachineView ? (
    available_display
  ) : (
    <QuantityChangeModal
      activatorText={available_display}
      disabled={discarded}
      horseVariantId={id}
      key={`qcm:${id}`}
      setErrorMessage={setErrorMessage}
      tracked={tracked}
    />
  );

  const committed_display: string = tracked ? (committed || 0).toString() : "-";
  const col5bMarkup = <span style={{ color: column_color }}>{committed_display}</span>;

  const col6Markup = weight ? <span style={{ color: column_color }}>{`${weight}g`}</span> : null;

  const col8Markup = <span style={{ color: column_color }}>{formatMoney(cost, userCurrency, 2)}</span>;

  const col9Markup = <span style={{ color: column_color }}>{formatMoney(value, userCurrency, 2)}</span>;

  const columnValues = {
    min: horseVariant.min,
    price: formatMoney(horseVariant.price, userCurrency, 2),
  };

  return (
    <IndexTable.Row id={(position + 1).toString()} key={`row:${horseVariant.id}`} position={position}>
      <IndexTable.Cell key={`cell:${position}:0`}>{col0Markup}</IndexTable.Cell>
      <IndexTable.Cell key={`cell:${position}:1`}>{col1Markup}</IndexTable.Cell>
      <IndexTable.Cell key={`cell:${position}:2`}>{col2Markup}</IndexTable.Cell>
      <IndexTable.Cell key={`cell:${position}:3`}>{col3Markup}</IndexTable.Cell>
      <IndexTable.Cell key={`cell:${position}:4`}>{col4Markup}</IndexTable.Cell>
      {showIncoming ? (
        <IndexTable.Cell>
          <Text as="span" tone={discarded ? "subdued" : undefined}>{horseVariant.incoming}</Text>
        </IndexTable.Cell>
      ) : null}
      <IndexTable.Cell key={`cell:${position}:5`}>{col5Markup}</IndexTable.Cell>
      {visibleLocationColumns.map((column) => (
        <IndexTable.Cell key={column.id}>
          <Text as="span" tone={discarded ? "subdued" : undefined}>
            {tracked ? (horseVariant.available_by_location[column.horseLocationId] ?? 0).toString() : "∞"}
          </Text>
        </IndexTable.Cell>
      ))}
      <IndexTable.Cell key={`cell:${position}:5b`}>{col5bMarkup}</IndexTable.Cell>
      {showOnHand ? (
        <IndexTable.Cell>
          <Text as="span" tone={discarded ? "subdued" : undefined}>
            {tracked ? (horseVariant.on_hand || 0).toString() : "-"}
          </Text>
        </IndexTable.Cell>
      ) : null}
      <IndexTable.Cell key={`cell:${position}:6`}>{col6Markup}</IndexTable.Cell>
      <IndexTable.Cell key={`cell:${position}:8`}>{col8Markup}</IndexTable.Cell>
      <IndexTable.Cell key={`cell:${position}:9`}>{col9Markup}</IndexTable.Cell>
      {visibleTrailingColumns.map((column) => (
        <IndexTable.Cell key={column.id}>
          {column.id === "warehouse_location" ? (
            horseVariant.warehouse_location && (
              <Box maxWidth="160px">
                <Tooltip content={horseVariant.warehouse_location}>
                  <Text as="span" tone={discarded ? "subdued" : undefined} truncate>
                    {horseVariant.warehouse_location}
                  </Text>
                </Tooltip>
              </Box>
            )
          ) : (
            <Text as="span" tone={discarded ? "subdued" : undefined}>{columnValues[column.id]}</Text>
          )}
        </IndexTable.Cell>
      ))}
    </IndexTable.Row>
  );
}
